import {
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Activity,
  AlertTriangle,
  Check,
  ChevronRight,
  CircleHelp,
  Clock3,
  Database,
  Layers3,
  ListChecks,
  MapPin,
  Radio,
  RefreshCw,
  ShieldCheck,
  Users,
} from "lucide-react";
import { fetchOperationsEstimate } from "../api";
import { evaluateToiletsForOperations, getActiveHazards } from "../engine";
import { formatDateTime, stateMeta } from "../format";
import { SPOT_OPTIONS } from "../spots";
import type {
  DataSnapshot,
  DataSource,
  EvaluatedToilet,
  OperationsEstimate,
  OperationsRiskArea,
  OperationsRiskLevel,
  Scenario,
  SourceHealth,
  SpotId,
  StatusObservation,
} from "../types";
import { StatusBadge } from "./StatusBadge";

const MapView = lazy(() => import("./MapView"));

const scenarios = ["normal", "event", "outage", "earthquake"] as const;

const scenarioLabels: Record<Scenario, string> = {
  normal: "現在",
  event: "イベント",
  outage: "断水",
  earthquake: "地震",
};

const riskLabels: Record<OperationsRiskLevel, string> = {
  adequate: "余力傾向",
  strained: "ひっ迫傾向",
  critical: "ひっ迫リスク高",
  unknown: "推定困難",
};

const riskPriority: Record<OperationsRiskLevel, number> = {
  critical: 0,
  strained: 1,
  unknown: 2,
  adequate: 3,
};

const layerLabels = {
  toilets: "トイレ",
  hazards: "ハザード",
  demandSupply: "需要・供給推定",
  reports: "利用報告",
  unknown: "情報不明",
} as const;

const healthLabels: Record<SourceHealth, string> = {
  healthy: "正常",
  stale: "更新注意",
  degraded: "一部障害",
  manual: "手動確認",
  permission_required: "利用許諾待ち",
  not_configured: "未接続",
};

const healthPriority: Record<SourceHealth, number> = {
  degraded: 0,
  stale: 1,
  permission_required: 2,
  not_configured: 3,
  manual: 4,
  healthy: 5,
};

type LayerName = keyof typeof layerLabels;

type ScenarioConfig = {
  scenario: Scenario;
  at: Date;
};

type RetainedEstimate = {
  estimate: OperationsEstimate;
  receivedAt: string;
};

type EstimateLoadState =
  | {
      status: "loading";
      requestKey: string;
      retained?: RetainedEstimate;
    }
  | {
      status: "loaded";
      requestKey: string;
      estimate: OperationsEstimate;
      receivedAt: string;
    }
  | {
      status: "error";
      requestKey: string;
      message: string;
      retained?: RetainedEstimate;
    };

const tokyoInputFormatter = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Tokyo",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

function formatTokyoDateTimeInput(date: Date) {
  const parts = new Map(
    tokyoInputFormatter
      .formatToParts(date)
      .filter(({ type }) => type !== "literal")
      .map(({ type, value }) => [type, value]),
  );
  return `${parts.get("year")}-${parts.get("month")}-${parts.get("day")}T${parts.get("hour")}:${parts.get("minute")}`;
}

function parseTokyoDateTimeInput(value: string) {
  const timestamp = Date.parse(`${value}:00+09:00`);
  return Number.isFinite(timestamp) ? new Date(timestamp) : undefined;
}

function qualityLabel(confidence: number) {
  if (confidence >= 0.75) return "高";
  if (confidence >= 0.5) return "中";
  return "低";
}

function formatIndex(value: number | null) {
  return value === null ? "算出できず" : value.toFixed(2);
}

function formatInterval(lower: number | null, point: number | null, upper: number | null) {
  if (lower === null || point === null || upper === null) return "算出できず";
  return `${lower.toFixed(2)}〜${upper.toFixed(2)}（代表 ${point.toFixed(2)}）`;
}

function pointInRing(point: [number, number], ring: number[][]) {
  let inside = false;
  for (let current = 0, previous = ring.length - 1; current < ring.length; previous = current++) {
    const currentPoint = ring[current];
    const previousPoint = ring[previous];
    if (!currentPoint || !previousPoint) continue;
    const [currentX, currentY] = currentPoint;
    const [previousX, previousY] = previousPoint;
    if (
      currentX === undefined ||
      currentY === undefined ||
      previousX === undefined ||
      previousY === undefined
    ) {
      continue;
    }
    const crosses =
      currentY > point[1] !== previousY > point[1] &&
      point[0] <
        ((previousX - currentX) * (point[1] - currentY)) /
          (previousY - currentY) +
          currentX;
    if (crosses) inside = !inside;
  }
  return inside;
}

function pointInPolygon(point: [number, number], polygon: number[][][]) {
  const outer = polygon[0];
  if (!outer || !pointInRing(point, outer)) return false;
  return polygon.slice(1).every((hole) => !pointInRing(point, hole));
}

function itemIsInRiskArea(item: EvaluatedToilet, area: OperationsRiskArea) {
  const point: [number, number] = [
    item.coordinates.longitude,
    item.coordinates.latitude,
  ];
  return area.geometry.type === "Polygon"
    ? pointInPolygon(point, area.geometry.coordinates)
    : area.geometry.coordinates.some((polygon) => pointInPolygon(point, polygon));
}

function sortSources(sources: DataSource[]) {
  return [...sources].sort(
    (left, right) =>
      healthPriority[left.health] - healthPriority[right.health] ||
      left.publisher.localeCompare(right.publisher, "ja"),
  );
}

export function OperationsDashboard({
  snapshot,
  observations,
  online,
  selectedSpotId,
  spotLoading,
  onSpotChange,
  onExit,
}: {
  snapshot: DataSnapshot;
  observations: StatusObservation[];
  online: boolean;
  selectedSpotId: SpotId;
  spotLoading: boolean;
  onSpotChange: (spotId: SpotId) => void | Promise<void>;
  onExit: () => void;
}) {
  const [simulationBaseTime] = useState(() => new Date());
  const [draftConfig, setDraftConfig] = useState<ScenarioConfig>(() => ({
    scenario: "normal",
    at: simulationBaseTime,
  }));
  const [appliedConfig, setAppliedConfig] = useState<ScenarioConfig>(() => ({
    scenario: "normal",
    at: simulationBaseTime,
  }));
  const [selectedId, setSelectedId] = useState(snapshot.toilets[0]?.id);
  const [selectedRiskAreaId, setSelectedRiskAreaId] = useState<string>();
  const [retryVersion, setRetryVersion] = useState(0);
  const [layerAnnouncement, setLayerAnnouncement] = useState("");
  const [layers, setLayers] = useState({
    toilets: true,
    hazards: true,
    demandSupply: true,
    reports: true,
    unknown: true,
  });
  const retainedEstimateByKey = useRef(new Map<string, RetainedEstimate>());

  const at = appliedConfig.at;
  const scenario = appliedConfig.scenario;
  const requestedAt = at.toISOString();
  const requestKey = `${snapshot.region.spotId}:${scenario}:${requestedAt}`;
  const [storedEstimateState, setEstimateState] = useState<EstimateLoadState>({
    status: "loading",
    requestKey,
  });
  const spotTransitioning =
    spotLoading || selectedSpotId !== snapshot.region.spotId;
  const estimateState: EstimateLoadState =
    !spotTransitioning && storedEstimateState.requestKey === requestKey
      ? storedEstimateState
      : {
          status: "loading",
          requestKey,
          retained: retainedEstimateByKey.current.get(requestKey),
        };
  const selectedSpot =
    SPOT_OPTIONS.find((spot) => spot.id === selectedSpotId) ??
    SPOT_OPTIONS.find((spot) => spot.id === snapshot.region.spotId);
  const selectedSpotName = selectedSpot?.name ?? snapshot.region.name;
  const draftChanged =
    draftConfig.scenario !== appliedConfig.scenario ||
    draftConfig.at.getTime() !== appliedConfig.at.getTime();

  useEffect(() => {
    const retained = retainedEstimateByKey.current.get(requestKey);
    if (spotTransitioning) {
      setEstimateState({ status: "loading", requestKey, retained });
      return;
    }
    if (!online) {
      setEstimateState({
        status: "error",
        requestKey,
        message: "オフラインのため、推定データを取得できません。",
        retained,
      });
      return;
    }

    const controller = new AbortController();
    setEstimateState({ status: "loading", requestKey, retained });
    void fetchOperationsEstimate(
      snapshot.region.spotId,
      scenario,
      new Date(requestedAt),
      { signal: controller.signal },
    )
      .then((estimate) => {
        if (controller.signal.aborted) return;
        const receivedAt = new Date().toISOString();
        retainedEstimateByKey.current.set(requestKey, { estimate, receivedAt });
        setEstimateState({
          status: "loaded",
          requestKey,
          estimate,
          receivedAt,
        });
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setEstimateState({
            status: "error",
            requestKey,
            message:
              "推定データを取得できませんでした。前回結果を確認するか再試行してください。",
            retained,
          });
        }
      });
    return () => controller.abort();
  }, [
    online,
    requestKey,
    requestedAt,
    retryVersion,
    scenario,
    snapshot.region.spotId,
    spotTransitioning,
  ]);

  useEffect(() => {
    setSelectedId(snapshot.toilets[0]?.id);
  }, [snapshot.region.spotId, snapshot.toilets]);

  const items = useMemo(
    () =>
      evaluateToiletsForOperations(snapshot, {
        at,
        scenario,
        origin: snapshot.region.center,
        needs: {
          wheelchair: false,
          ostomate: false,
          baby_changing: false,
          openNow: false,
        },
        observations,
        online,
      }),
    [at.getTime(), online, observations, scenario, snapshot],
  );
  const normalItems = useMemo(
    () =>
      evaluateToiletsForOperations(snapshot, {
        at,
        scenario: "normal",
        origin: snapshot.region.center,
        needs: {
          wheelchair: false,
          ostomate: false,
          baby_changing: false,
          openNow: false,
        },
        observations,
        online,
      }),
    [at.getTime(), online, observations, snapshot],
  );
  const normalStateById = useMemo(
    () => new Map(normalItems.map((item) => [item.id, item.state])),
    [normalItems],
  );
  const visibleItems = spotTransitioning ? [] : items;
  const listedItems = layers.unknown
    ? visibleItems
    : visibleItems.filter((item) => item.state !== "unknown");
  const visibleSources = useMemo(
    () => (spotTransitioning ? [] : sortSources(snapshot.sources)),
    [snapshot.sources, spotTransitioning],
  );
  const activeCommunityReports = useMemo(
    () =>
      observations.filter(
        (observation) =>
          observation.type === "community" &&
          Date.parse(observation.observedAt) <= at.getTime() &&
          Date.parse(observation.validUntil) >= at.getTime(),
      ),
    [at.getTime(), observations],
  );
  const reportedIds = useMemo(
    () => new Set(activeCommunityReports.map((report) => report.toiletId)),
    [activeCommunityReports],
  );
  const hazards =
    !spotTransitioning && layers.hazards
      ? getActiveHazards(snapshot, scenario, at)
      : [];
  const retainedEstimate =
    estimateState.status === "loading" || estimateState.status === "error"
      ? estimateState.retained
      : undefined;
  const estimate =
    estimateState.status === "loaded"
      ? estimateState.estimate
      : retainedEstimate?.estimate;
  const lastSuccessAt =
    estimateState.status === "loaded"
      ? estimateState.receivedAt
      : retainedEstimate?.receivedAt;
  const isRetainedEstimate = estimate !== undefined && estimateState.status !== "loaded";
  const riskAreas = estimate?.riskAreas.features ?? [];
  const sortedRiskAreas = useMemo(
    () =>
      [...riskAreas].sort(
        (left, right) =>
          Number(right.properties.problemLikely === true) -
            Number(left.properties.problemLikely === true) ||
          riskPriority[left.properties.riskLevel] -
            riskPriority[right.properties.riskLevel] ||
          left.properties.areaId.localeCompare(right.properties.areaId, "ja"),
      ),
    [riskAreas],
  );
  const selectedRiskArea = riskAreas.find(
    (area) => area.properties.areaId === selectedRiskAreaId,
  );
  const facilityItems = selectedRiskArea
    ? listedItems.filter((item) => itemIsInRiskArea(item, selectedRiskArea))
    : listedItems;
  const selected =
    facilityItems.find((item) => item.id === selectedId) ??
    listedItems.find((item) => item.id === selectedId) ??
    facilityItems[0] ??
    listedItems[0];
  const changedFacilityItems = visibleItems.filter(
    (item) => normalStateById.get(item.id) !== item.state,
  );
  const unknownFacilityItems = visibleItems.filter((item) => {
    if (item.state === "unknown") return true;
    return item.sourceIds.some((sourceId) => {
      const source = snapshot.sources.find((candidate) => candidate.id === sourceId);
      return source ? source.health !== "healthy" : false;
    });
  });
  const problemAreas = sortedRiskAreas.filter(
    (area) => area.properties.problemLikely === true,
  );
  const criticalAreas = sortedRiskAreas.filter(
    (area) => area.properties.riskLevel === "critical",
  );
  const counts = {
    unknown: visibleItems.filter((item) => item.state === "unknown").length,
    limited: visibleItems.filter((item) => item.state === "limited").length,
    difficult: visibleItems.filter(
      (item) =>
        item.state === "likely_unavailable" ||
        item.state === "unavailable_confirmed",
    ).length,
    stale: visibleSources.filter((source) =>
      ["stale", "degraded", "not_configured"].includes(source.health),
    ).length,
  };

  const selectRiskArea = (areaId: string) => {
    setSelectedRiskAreaId(areaId);
    const area = riskAreas.find((candidate) => candidate.properties.areaId === areaId);
    const firstFacility = area
      ? listedItems.find((item) => itemIsInRiskArea(item, area))
      : undefined;
    if (firstFacility) setSelectedId(firstFacility.id);
  };

  const applyDraft = () => {
    if (!draftChanged || spotTransitioning) return;
    setAppliedConfig({
      scenario: draftConfig.scenario,
      at: new Date(draftConfig.at),
    });
    setSelectedRiskAreaId(undefined);
  };

  const handleLayerChange = (layer: LayerName, enabled: boolean) => {
    setLayers((current) => ({ ...current, [layer]: enabled }));
    if (layer === "demandSupply" && !enabled) setSelectedRiskAreaId(undefined);
    setLayerAnnouncement(
      `${layerLabels[layer]}レイヤーを${enabled ? "表示" : "非表示"}にしました。`,
    );
  };

  const handleSpotChange = (spotId: SpotId) => {
    setSelectedRiskAreaId(undefined);
    void onSpotChange(spotId);
  };

  const actions = [
    {
      id: "facility-check",
      title: "施設の利用可否を確認",
      detail: "状況不明または情報更新に注意が必要な施設を表示します。",
      target: `${unknownFacilityItems.length}施設`,
      count: unknownFacilityItems.length,
      onSelect: () => {
        const first = unknownFacilityItems[0];
        if (!first) return;
        setSelectedRiskAreaId(undefined);
        setSelectedId(first.id);
      },
    },
    {
      id: "problem-area",
      title: "問題兆候の区域を確認",
      detail: "独立した根拠で問題発生の強い兆候がある区域へ移動します。",
      target: `${problemAreas.length}区域`,
      count: problemAreas.length,
      onSelect: () => {
        const first = problemAreas[0];
        if (first) selectRiskArea(first.properties.areaId);
      },
    },
    {
      id: "critical-area",
      title: "高リスク区域の供給候補を確認",
      detail: "平常時比でひっ迫リスクが高い区域と区域内施設を表示します。",
      target: `${criticalAreas.length}区域`,
      count: criticalAreas.length,
      onSelect: () => {
        const first = criticalAreas[0];
        if (first) selectRiskArea(first.properties.areaId);
      },
    },
  ];
  const estimateAnnouncement =
    estimateState.status === "loading"
      ? isRetainedEstimate
        ? "推定を再計算中です。前回成功時の結果を表示しています。"
        : "需要と供給の推定を計算中です。"
      : estimateState.status === "error"
        ? estimateState.message
        : estimateState.estimate.status === "insufficient_data"
          ? "推定に必要なデータが不足しています。"
          : `${estimateState.estimate.summary.areaCount}区域の推定が完了しました。問題発生の強い兆候は${estimateState.estimate.summary.problemLikelyAreas}区域です。`;

  return (
    <div className="operations-app">
      <a className="skip-link" href="#operations-main-content">
        本文へ移動
      </a>
      <header id="operations-top" className="operations-header">
        <button
          className="operations-brand"
          type="button"
          onClick={onExit}
          aria-label="東京トイレマップ 利用者向けトップページへ戻る"
        >
          <span>東京</span>
          <b>東京トイレマップ Operations</b>
          <small>利用者画面へ戻る</small>
        </button>
        <div className="simulation-ribbon">
          <AlertTriangle aria-hidden="true" />
          <b>訓練・シミュレーション</b>
          <span>実際の災害情報ではありません</span>
        </div>
      </header>

      <main id="operations-main-content" tabIndex={-1}>
        <h1 className="sr-only">東京トイレマップ 運営判断支援ダッシュボード</h1>
        <section
          className="area-switcher operations-area-switcher"
          aria-labelledby="operations-area-switcher-label"
          aria-busy={spotLoading}
        >
          <b id="operations-area-switcher-label">対象エリア</b>
          <div role="group" aria-labelledby="operations-area-switcher-label">
            {SPOT_OPTIONS.map((spot) => (
              <button
                key={spot.id}
                type="button"
                className={selectedSpotId === spot.id ? "is-active" : ""}
                aria-pressed={selectedSpotId === spot.id}
                disabled={spotLoading}
                onClick={() => handleSpotChange(spot.id)}
              >
                {spot.name}
              </button>
            ))}
          </div>
          {spotLoading && (
            <span role="status" aria-live="polite">
              {selectedSpotName}の運営データを読み込んでいます
            </span>
          )}
        </section>

        <section className="operations-controls" aria-labelledby="scenario-draft-title">
          <b id="scenario-draft-title">シナリオ下書き</b>
          <div className="scenario-choice" role="group" aria-label="シナリオ種別">
            {scenarios.map((value) => (
              <button
                type="button"
                key={value}
                className={draftConfig.scenario === value ? "is-active" : ""}
                onClick={() =>
                  setDraftConfig((current) => ({ ...current, scenario: value }))
                }
                aria-pressed={draftConfig.scenario === value}
              >
                {scenarioLabels[value]}
              </button>
            ))}
          </div>
          <div className="scenario-time-presets" role="group" aria-label="対象日時の近道">
            {[0, 60, 180].map((minutes) => {
              const presetTime = simulationBaseTime.getTime() + minutes * 60_000;
              const active = draftConfig.at.getTime() === presetTime;
              return (
                <button
                  type="button"
                  key={minutes}
                  className={active ? "is-active" : ""}
                  onClick={() =>
                    setDraftConfig((current) => ({
                      ...current,
                      at: new Date(presetTime),
                    }))
                  }
                  aria-pressed={active}
                >
                  <Clock3 aria-hidden="true" />
                  {minutes === 0 ? "現在" : `+${minutes / 60}時間`}
                </button>
              );
            })}
          </div>
          <label className="scenario-datetime">
            <span>対象日時（JST）</span>
            <input
              type="datetime-local"
              value={formatTokyoDateTimeInput(draftConfig.at)}
              onChange={(event) => {
                const parsed = parseTokyoDateTimeInput(event.target.value);
                if (parsed) {
                  setDraftConfig((current) => ({ ...current, at: parsed }));
                }
              }}
            />
          </label>
          <button
            type="button"
            className="apply-scenario-button"
            disabled={!draftChanged || spotTransitioning}
            onClick={applyDraft}
          >
            下書きを適用
          </button>
        </section>

        <section className="applied-scenario-bar" aria-live="polite">
          <span>適用中</span>
          <b>{scenarioLabels[scenario]}</b>
          <strong>{formatDateTime(at)} JST</strong>
          <small>{selectedSpotName}</small>
          {draftChanged && <em>未適用の変更があります</em>}
        </section>

        <div className="operations-workspace" aria-busy={spotTransitioning}>
          <aside className="layer-rail" aria-label="地図レイヤーと供給状態">
            <h2>
              <Layers3 aria-hidden="true" />
              レイヤー
            </h2>
            {(Object.entries(layers) as [LayerName, boolean][]).map(
              ([layer, enabled]) => {
                const reportUnavailable =
                  layer === "reports" && activeCommunityReports.length === 0;
                return (
                  <label key={layer} className={reportUnavailable ? "is-disabled" : ""}>
                    <input
                      type="checkbox"
                      checked={enabled && !reportUnavailable}
                      disabled={reportUnavailable}
                      onChange={(event) =>
                        handleLayerChange(layer, event.target.checked)
                      }
                    />
                    <span>{layerLabels[layer]}</span>
                    {reportUnavailable && <small>有効な報告なし</small>}
                  </label>
                );
              },
            )}
            <span className="sr-only" role="status" aria-live="polite">
              {layerAnnouncement}
            </span>
            <section>
              <h3>平常時比の需給ひっ迫</h3>
              <p>
                <i className="supply-ok" /> 余力傾向
              </p>
              <p>
                <i className="supply-busy" /> ひっ迫傾向
              </p>
              <p>
                <i className="supply-danger" /> ひっ迫リスク高
              </p>
              <p>
                <i className="supply-unknown" /> 推定困難
              </p>
              <small>過去人流・災害データと施設容量から推定</small>
            </section>
          </aside>

          <section className="operations-overview" aria-label="総合要約">
            <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
              {estimateAnnouncement}
            </div>
            <section
              className="operations-estimate"
              aria-labelledby="operations-estimate-title"
              aria-busy={estimateState.status === "loading"}
            >
              <h2 id="operations-estimate-title">
                <Activity aria-hidden="true" />
                需給ひっ迫リスク推定
              </h2>
              {estimateState.status === "loading" && (
                <div className="estimate-message is-loading">
                  <RefreshCw aria-hidden="true" />
                  <span>
                    <b>推定を計算中</b>
                    <small>
                      {retainedEstimate
                        ? "同じ条件の前回成功結果を保持して再計算しています。"
                        : "過去人流・災害データと施設容量を照合しています。"}
                    </small>
                  </span>
                </div>
              )}
              {estimateState.status === "error" && (
                <div className="estimate-message is-error">
                  <AlertTriangle aria-hidden="true" />
                  <span>
                    <b>推定を更新できません</b>
                    <small>{estimateState.message}</small>
                    <button
                      type="button"
                      disabled={!online}
                      onClick={() => setRetryVersion((current) => current + 1)}
                    >
                      <RefreshCw aria-hidden="true" />
                      再試行
                    </button>
                  </span>
                </div>
              )}
              {isRetainedEstimate && (
                <p className="retained-estimate-note" role="note">
                  前回成功時の結果です。更新完了まで判断材料としてのみ確認してください。
                </p>
              )}
              {lastSuccessAt && (
                <p className="estimate-last-success">
                  最終成功 {formatDateTime(lastSuccessAt)}
                </p>
              )}
              {estimate?.status === "insufficient_data" && (
                <>
                  <div className="estimate-message is-insufficient">
                    <CircleHelp aria-hidden="true" />
                    <span>
                      <b>推定に必要なデータが不足</b>
                      <small>
                        判定できない区域は、地図上で灰色の「推定困難」として区別します。
                      </small>
                    </span>
                  </div>
                  <dl className="estimate-metadata">
                    <div>
                      <dt>全対象区域</dt>
                      <dd>{estimate.summary.areaCount}区域</dd>
                    </div>
                    <div>
                      <dt>推定困難</dt>
                      <dd>{estimate.summary.unknownAreas}区域</dd>
                    </div>
                    <div>
                      <dt>データ充足度</dt>
                      <dd>{qualityLabel(estimate.summary.confidence)}</dd>
                    </div>
                  </dl>
                </>
              )}
              {estimate && estimate.status !== "insufficient_data" && (
                <>
                  <p className="estimate-result-heading">
                    <b>{estimate.status === "ready" ? "推定完了" : "一部データで推定"}</b>
                    <span>
                      問題発生の強い兆候 <strong>{estimate.summary.problemLikelyAreas}区域</strong>
                    </span>
                  </p>
                  <div className="estimate-area-counts">
                    <span>
                      <i className="supply-ok" aria-hidden="true" />
                      <b>{estimate.summary.adequateAreas}</b>
                      余力傾向
                    </span>
                    <span>
                      <i className="supply-busy" aria-hidden="true" />
                      <b>{estimate.summary.strainedAreas}</b>
                      ひっ迫傾向
                    </span>
                    <span>
                      <i className="supply-danger" aria-hidden="true" />
                      <b>{estimate.summary.criticalAreas}</b>
                      高リスク
                    </span>
                    <span>
                      <i className="supply-unknown" aria-hidden="true" />
                      <b>{estimate.summary.unknownAreas}</b>
                      推定困難
                    </span>
                  </div>
                  <dl className="estimate-metadata">
                    <div>
                      <dt>全対象区域</dt>
                      <dd>{estimate.summary.areaCount}区域</dd>
                    </div>
                    <div>
                      <dt>データ充足度</dt>
                      <dd>{qualityLabel(estimate.summary.confidence)}</dd>
                    </div>
                    <div>
                      <dt>モデル</dt>
                      <dd>{estimate.model.version}</dd>
                    </div>
                    <div>
                      <dt>データ基準</dt>
                      <dd>
                        {estimate.dataCutoffAt
                          ? formatDateTime(estimate.dataCutoffAt)
                          : "確認できず"}
                      </dd>
                    </div>
                  </dl>
                </>
              )}
              {estimate && (
                <>
                  <p className="confidence-explanation">
                    データ充足度は根拠の量・容量判明率・鮮度を示す品質区分で、問題が起きる確率ではありません。
                  </p>
                  <div className="scenario-delta-summary">
                    <span>同時刻の平常時比</span>
                    <b>{changedFacilityItems.length}施設で状態変化</b>
                    <small>
                      {scenario === "normal"
                        ? "現在モードのため差分はありません。"
                        : "施設一覧で平常時からの変化を確認できます。"}
                    </small>
                  </div>
                  {estimate.limitations.length > 0 && (
                    <details className="estimate-limitations">
                      <summary>解釈上の注意</summary>
                      <ul>
                        {estimate.limitations.map((limitation) => (
                          <li key={limitation}>{limitation}</li>
                        ))}
                      </ul>
                    </details>
                  )}
                </>
              )}
            </section>
          </section>

          <section className="operations-main" aria-label="推定地図">
            <Suspense
              fallback={
                <section
                  className="map-shell map-loading"
                  aria-label={`${snapshot.region.name}の地図を読み込み中`}
                >
                  地図を読み込んでいます…
                </section>
              }
            >
              <MapView
                key={selectedSpotId}
                items={layers.toilets ? listedItems : []}
                hazards={hazards}
                riskAreas={layers.demandSupply && estimate ? riskAreas : []}
                reportedIds={layers.reports ? reportedIds : undefined}
                origin={
                  spotTransitioning && selectedSpot
                    ? selectedSpot.center
                    : snapshot.region.center
                }
                originLabel={`${selectedSpotName}の運営基準点`}
                originApproximate
                selectedId={selected?.id}
                selectedRiskAreaId={selectedRiskArea?.properties.areaId}
                online={online}
                regionName={selectedSpotName}
                onSelect={setSelectedId}
                onRiskAreaSelect={selectRiskArea}
                onClearRoute={() => undefined}
              />
            </Suspense>
          </section>

          <aside className="decision-rail" aria-label="運営対応と状態サマリー">
            <section className="operations-actions">
              <h2>
                <ListChecks aria-hidden="true" />
                対応候補
              </h2>
              <p>候補表示だけを行い、施設状態や外部システムは変更しません。</p>
              <ol>
                {actions.map((action, index) => (
                  <li key={action.id}>
                    <button
                      type="button"
                      disabled={action.count === 0 || spotTransitioning}
                      onClick={action.onSelect}
                    >
                      <b>{index + 1}</b>
                      <span>
                        <strong>{action.title}</strong>
                        <small>{action.detail}</small>
                        <em>{action.count === 0 ? "対象なし" : action.target}</em>
                      </span>
                      <ChevronRight aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ol>
            </section>

            <section className="status-summary">
              <h2>状態サマリー</h2>
              <div>
                <span>
                  <CircleHelp aria-hidden="true" />
                  <b>{counts.unknown}</b>
                  未確認
                </span>
                <span>
                  <Users aria-hidden="true" />
                  <b>{counts.limited}</b>
                  混雑
                </span>
                <span>
                  <AlertTriangle aria-hidden="true" />
                  <b>{counts.difficult}</b>
                  利用困難
                </span>
                <span>
                  <Clock3 aria-hidden="true" />
                  <b>{counts.stale}</b>
                  情報注意
                </span>
              </div>
            </section>

            {riskAreas.length > 0 && (
              <section className="risk-area-selector" aria-labelledby="risk-area-list-title">
                <div className="section-title">
                  <h2 id="risk-area-list-title">推定区域</h2>
                  <span>{riskAreas.length}区域</span>
                </div>
                <p>地図または一覧から区域を選ぶと、根拠と区域内施設を確認できます。</p>
                <div
                  className="risk-area-buttons"
                  role="group"
                  aria-label="推定区域を選択"
                >
                  {sortedRiskAreas.map((area) => {
                    const originalIndex = riskAreas.findIndex(
                      (candidate) =>
                        candidate.properties.areaId === area.properties.areaId,
                    );
                    const active = selectedRiskArea?.properties.areaId === area.properties.areaId;
                    return (
                      <button
                        type="button"
                        key={area.properties.areaId}
                        className={`risk-${area.properties.riskLevel}${active ? " is-selected" : ""}`}
                        aria-pressed={active}
                        onClick={() => selectRiskArea(area.properties.areaId)}
                      >
                        <span>区域 {originalIndex + 1}</span>
                        <b>{riskLabels[area.properties.riskLevel]}</b>
                        <small>
                          {area.properties.problemLikely === true
                            ? "問題発生の強い兆候あり"
                            : area.properties.problemLikely === false
                              ? "強い兆候なし"
                              : "問題兆候は判定できず"}
                        </small>
                      </button>
                    );
                  })}
                </div>
              </section>
            )}

            {selectedRiskArea && (
              <section className="risk-area-inspector">
                <div className="section-title">
                  <h2>選択中の推定区域</h2>
                  <button type="button" onClick={() => setSelectedRiskAreaId(undefined)}>
                    選択解除
                  </button>
                </div>
                <strong className={`risk-heading risk-${selectedRiskArea.properties.riskLevel}`}>
                  {riskLabels[selectedRiskArea.properties.riskLevel]}
                </strong>
                <p>{selectedRiskArea.properties.summary}</p>
                <dl className="risk-intervals">
                  <div>
                    <dt>需要指数</dt>
                    <dd>
                      {formatInterval(
                        selectedRiskArea.properties.demandLower,
                        selectedRiskArea.properties.demandPoint,
                        selectedRiskArea.properties.demandUpper,
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt>供給指数</dt>
                    <dd>
                      {formatInterval(
                        selectedRiskArea.properties.supplyLower,
                        selectedRiskArea.properties.supplyPoint,
                        selectedRiskArea.properties.supplyUpper,
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt>需給ひっ迫</dt>
                    <dd>
                      {formatInterval(
                        selectedRiskArea.properties.pressureLower,
                        selectedRiskArea.properties.pressurePoint,
                        selectedRiskArea.properties.pressureUpper,
                      )}
                    </dd>
                  </div>
                  <div>
                    <dt>問題発生の強い兆候</dt>
                    <dd>
                      {selectedRiskArea.properties.problemLikely === true
                        ? "あり"
                        : selectedRiskArea.properties.problemLikely === false
                          ? "なし"
                          : "判定できず"}
                    </dd>
                  </div>
                  <div>
                    <dt>データ充足度</dt>
                    <dd>{qualityLabel(selectedRiskArea.properties.confidence)}</dd>
                  </div>
                  <div>
                    <dt>区域内施設</dt>
                    <dd>
                      {selectedRiskArea.properties.facilityCount}施設（容量判明
                      {selectedRiskArea.properties.knownCapacityCount}・不明
                      {selectedRiskArea.properties.unknownCapacityCount}）
                    </dd>
                  </div>
                  <div>
                    <dt>過去の類似イベント</dt>
                    <dd>{selectedRiskArea.properties.historicalEventCount}件</dd>
                  </div>
                  <div>
                    <dt>過去の関連災害</dt>
                    <dd>{selectedRiskArea.properties.historicalHazardCount}件</dd>
                  </div>
                </dl>
                <p className="risk-index-note">
                  指数は平常時を1とした相対値です。代表値は
                  {formatIndex(selectedRiskArea.properties.pressurePoint)}です。
                </p>
                <div className="risk-lineage">
                  <b>使用した出典</b>
                  {selectedRiskArea.properties.sourceIds.length > 0 ? (
                    <ul>
                      {selectedRiskArea.properties.sourceIds.map((sourceId) => {
                        const source = snapshot.sources.find(
                          (candidate) => candidate.id === sourceId,
                        );
                        return (
                          <li key={sourceId}>
                            {source
                              ? `${source.publisher}「${source.datasetName}」`
                              : sourceId}
                          </li>
                        );
                      })}
                    </ul>
                  ) : (
                    <small>出典を特定できません</small>
                  )}
                </div>
                {selectedRiskArea.properties.limitations.length > 0 && (
                  <div className="risk-limitations">
                    <b>区域固有の制約</b>
                    <ul>
                      {selectedRiskArea.properties.limitations.map((limitation) => (
                        <li key={limitation}>{limitation}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </section>
            )}

            {selected && (
              <section className="operations-inspector">
                <div className="section-title">
                  <h2>選択中の施設</h2>
                  <MapPin aria-hidden="true" />
                </div>
                <StatusBadge state={selected.state} />
                <h3>{selected.name}</h3>
                <p>{selected.reasons[0]}</p>
                <dl>
                  <div>
                    <dt>
                      <Radio aria-hidden="true" />
                      最終確認
                    </dt>
                    <dd>{selected.freshnessLabel}</dd>
                  </div>
                  <div>
                    <dt>
                      <Users aria-hidden="true" />
                      観測・根拠
                    </dt>
                    <dd>{selected.evidence.length}件</dd>
                  </div>
                  <div>
                    <dt>
                      <Activity aria-hidden="true" />
                      混雑
                    </dt>
                    <dd>{selected.queueLabel}</dd>
                  </div>
                </dl>
                <div className="primary-button is-static">
                  <ShieldCheck aria-hidden="true" />
                  確認依頼候補
                </div>
                <small>候補表示のみで、施設状態は変更しません。</small>
              </section>
            )}
          </aside>

          <section className="operations-auxiliary" aria-label="施設とデータソース">
            <section className="facility-table-shell">
              <div className="section-title">
                <h2 id="operations-facility-title">施設状態一覧</h2>
                <span>
                  {spotTransitioning
                    ? "読込中"
                    : selectedRiskArea
                      ? `選択区域内 ${facilityItems.length}施設`
                      : `${listedItems.length}施設`}
                </span>
              </div>
              {selectedRiskArea && (
                <button
                  type="button"
                  className="clear-area-filter"
                  onClick={() => setSelectedRiskAreaId(undefined)}
                >
                  全区域の施設へ戻す
                </button>
              )}
              <div
                id="operations-facility-table"
                className="facility-table"
                role="region"
                aria-labelledby="operations-facility-title"
              >
                <div className="facility-table-head" aria-hidden="true">
                  <span>施設</span>
                  <span>状態（平常時比）</span>
                  <span>主な根拠</span>
                  <span>最終確認</span>
                  <span>対応</span>
                </div>
                {facilityItems.map((item, index) => {
                  const normalState = normalStateById.get(item.id);
                  const changed = normalState !== undefined && normalState !== item.state;
                  return (
                    <button
                      type="button"
                      key={item.id}
                      className={`${selected?.id === item.id ? "is-selected" : ""}${changed ? " has-state-change" : ""}`}
                      onClick={() => setSelectedId(item.id)}
                    >
                      <span data-label="施設">
                        <b>{index + 1}</b>
                        <i>
                          <strong>{item.name}</strong>
                          <small>徒歩約{item.walkingMinutes}分</small>
                        </i>
                      </span>
                      <span data-label="状態（平常時比）">
                        {changed && normalState ? (
                          <small className="state-delta">
                            {stateMeta[normalState].shortLabel} →
                          </small>
                        ) : null}
                        <StatusBadge state={item.state} compact />
                      </span>
                      <span data-label="主な根拠">{item.reasons[0]}</span>
                      <span data-label="最終確認">{item.freshnessLabel}</span>
                      <span data-label="対応">
                        詳細確認
                        <ChevronRight aria-hidden="true" />
                      </span>
                    </button>
                  );
                })}
                {facilityItems.length === 0 && !spotTransitioning && (
                  <p className="operations-empty-state">選択条件に該当する施設はありません。</p>
                )}
              </div>
            </section>

            <section className="source-health">
              <div className="section-title">
                <h2 id="source-health-title">データソースの健全性</h2>
                <span>{visibleSources.length}件・要確認順</span>
              </div>
              <div
                className="source-health-table"
                role="region"
                aria-labelledby="source-health-title"
              >
                {visibleSources.map((source) => (
                  <div key={source.id}>
                    <span data-label="データソース">
                      <Database aria-hidden="true" />
                      <b>{source.publisher}</b>
                      <small>{source.datasetName}</small>
                    </span>
                    <span data-label="公式更新">
                      公式更新
                      <b>
                        {source.publisherUpdatedAt
                          ? formatDateTime(source.publisherUpdatedAt)
                          : "手動確認"}
                      </b>
                    </span>
                    <span data-label="アプリ取得">
                      アプリ取得
                      <b>
                        {source.fetchedAt
                          ? formatDateTime(source.fetchedAt)
                          : "未取得"}
                      </b>
                    </span>
                    <span
                      data-label="状態"
                      className={`source-state health-${source.health}`}
                    >
                      {source.health === "healthy" ? (
                        <Check aria-hidden="true" />
                      ) : (
                        <CircleHelp aria-hidden="true" />
                      )}
                      {healthLabels[source.health]}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          </section>
        </div>
      </main>

      <footer className="operations-footer">
        <span>
          <AlertTriangle aria-hidden="true" />
          このダッシュボードは訓練・シミュレーションです。実災害時は自治体・施設の公式情報を確認してください。
        </span>
        <nav aria-label="サイト情報">
          <a href="/site-information.html#policy">サイトポリシー</a>
          <a href="/site-information.html#accessibility">アクセシビリティ方針</a>
          <a href="/site-information.html#privacy">個人情報保護</a>
        </nav>
      </footer>
    </div>
  );
}
